import { useAtomValue } from 'jotai'
import { useState } from 'react'
import {
	MiniTabbedWidgetWrapper,
	WidgetInputTextField,
	WidgetLabel,
	WidgetSelectGroup,
	type WidgetSelectGroupItem,
} from '../../../../rapid-ui'
import { externalRefAtoms } from '../../atoms'
import { useCanvasStore } from '../../canvas-store'
import type { Layer, TextContentMode, TextRefTarget } from '../../data/v1'
import { BoxTabIcon, ContentTabIcon, TextFormattingTabIcon } from '../../glyphs/tabs'
import { useDesignerActions } from '../../use-designer-actions'
import { useExternalCanvasDataActions } from '../../use-external-canvas-data-actions'
import { BoxInspector } from './box'
import { FloatControls } from './float-controls'
import { TextFormattingInspector } from './text-formatting'
import { TextHighlightInspector } from './text-highlight'

const contentModeItems: WidgetSelectGroupItem<TextContentMode>[] = [
	{ id: 'ref', label: 'Reference', render: () => <span className="text-xs">Ref</span> },
	{ id: 'custom', label: 'Custom', render: () => <span className="text-xs">Custom</span> },
]

const refTargetItems: WidgetSelectGroupItem<TextRefTarget>[] = [
	{ id: 'title', label: 'Title', render: () => <span className="text-xs">Title</span> },
	{ id: 'subtitle', label: 'Subtitle', render: () => <span className="text-xs">Subtitle</span> },
	{ id: 'cta', label: 'CTA', render: () => <span className="text-xs">CTA</span> },
	{ id: 'location', label: 'Location', render: () => <span className="text-xs">Location</span> },
]

function RefValueEditor({ refTo }: { refTo: TextRefTarget }) {
	const store = useCanvasStore()
	const value = useAtomValue(externalRefAtoms[refTo], { store })
	const { updateExternalField } = useExternalCanvasDataActions()

	return (
		<WidgetLabel label="Value">
			<WidgetInputTextField
				value={value}
				onChange={(v) => updateExternalField(refTo, v)}
				placeholder={`Enter ${refTo}...`}
			/>
		</WidgetLabel>
	)
}

export type TextLayerInspectorProps = {
	layer: Extract<Layer, { type: 'text' }>
}

export function TextLayerInspector({ layer }: TextLayerInspectorProps) {
	const { updateLayer } = useDesignerActions()
	const [tab, setTab] = useState<'content' | 'formatting' | 'style'>('content')

	return (
		<>
			<FloatControls layer={layer} />
			<MiniTabbedWidgetWrapper
				value={tab}
				onChange={setTab}
				tabs={[
					{
						id: 'content',
						icon: <ContentTabIcon />,
						label: 'Content',
						children: (
							<div className="flex flex-col gap-2">
								<WidgetLabel label="Source">
									<WidgetSelectGroup
										value={layer.content}
										onChange={(value) => updateLayer(layer.id, { content: value })}
										items={contentModeItems}
									/>
								</WidgetLabel>

								{layer.content === 'ref'
									? (
										<>
											<WidgetLabel label="Reference">
												<WidgetSelectGroup
													value={layer.refTo}
													onChange={(value) => updateLayer(layer.id, { refTo: value })}
													items={refTargetItems}
												/>
											</WidgetLabel>
											<RefValueEditor refTo={layer.refTo} />
										</>
									)
									: (
										<WidgetLabel label="Text">
											<WidgetInputTextField
												value={layer.customValue}
												onChange={(value) => updateLayer(layer.id, { customValue: value })}
												placeholder="Enter text..."
											/>
										</WidgetLabel>
									)}
							</div>
						),
					},
					{
						id: 'formatting',
						icon: <TextFormattingTabIcon />,
						label: 'Formatting',
						children: (
							<div className="flex flex-col gap-2">
								<TextFormattingInspector
									textAlignMode={layer.textAlignMode}
									textAlignValue={layer.textAlignValue}
									onTextAlignModeChange={(m) => updateLayer(layer.id, { textAlignMode: m })}
									onTextAlignValueChange={(v) => updateLayer(layer.id, { textAlignValue: v })}
									textStyle={layer.textStyle}
									onTextStyleChange={(changes) =>
										updateLayer(layer.id, (prev) => ({
											textStyle: { ...(prev as typeof layer).textStyle, ...changes },
										}))}
								/>

								<TextHighlightInspector
									value={layer.textHighlight}
									onChange={(changes, options) =>
										updateLayer(layer.id, (prev) => ({
											textHighlight: { ...(prev as typeof layer).textHighlight, ...changes },
										}), options)}
								/>
							</div>
						),
					},
					{
						id: 'style',
						icon: <BoxTabIcon />,
						label: 'Box',
						children: (
							<BoxInspector
								layerId={layer.id}
								background={layer.background}
								onBackgroundChange={(updates, options) =>
									updateLayer(layer.id, (prev) => ({
										background: { ...(prev as typeof layer).background, ...updates },
									}), options)}
								borderRadius={layer.borderRadius}
								onBorderRadiusChange={(borderRadius) => updateLayer(layer.id, { borderRadius })}
								borderRadiusCorners={layer.borderRadiusCorners}
								onBorderRadiusCornersChange={(borderRadiusCorners) =>
									updateLayer(layer.id, { borderRadiusCorners })}
								spacing={layer.spacing}
								onSpacingChange={(spacing) => updateLayer(layer.id, { spacing })}
								onTransientSpacingChange={(spacing) =>
									updateLayer(layer.id, { spacing }, { transient: true })}
							/>
						),
					},
				]}
			/>
		</>
	)
}
